import React, { Suspense } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';

interface LoadingBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}

const DefaultLoadingSkeleton: React.FC = () => (
  <Card className="w-full">
    <CardContent className="p-6 space-y-4">
      <div className="flex items-center space-x-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </CardContent>
  </Card>
);

const DefaultSpinner: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center p-8 space-y-4">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
    {message && (
      <p className="text-sm text-muted-foreground">{message}</p>
    )}
  </div>
);

export const LoadingBoundary: React.FC<LoadingBoundaryProps> = ({
  children,
  fallback,
  className
}) => {
  const defaultFallback = fallback || <DefaultLoadingSkeleton />;

  return (
    <div className={className}>
      <Suspense fallback={defaultFallback}>
        {children}
      </Suspense>
    </div>
  );
};

export const FullPageLoader: React.FC<{ message?: string }> = ({ message = "Loading..." }) => (
  <div className="min-h-screen flex items-center justify-center">
    <DefaultSpinner message={message} />
  </div>
);

export { DefaultLoadingSkeleton, DefaultSpinner };